import { put, take, spawn, all, select, call } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'

// Redux Actions
import { featuresActions } from 'APP/Redux/FeaturesSlice'

// Services
import { ldClient, initLD } from 'APP/Services/LaunchDarkly'
import { LD_FEATURES } from 'APP/Helpers/ldFeatures'
import type { RootState } from 'APP/Store/CreateStore'
import { FeaturesState } from 'APP/Store/Features/types'

interface WebLDClient {
  ldClientInstance: {
    on: (event: string, callback: () => void) => void
    off: (event: string) => void
    variation: (key: string, defaultValue: unknown) => Promise<unknown>
  }
}

export const selectFeatures = (state: RootState): FeaturesState => state?.features || {}

export function* featuresMonitor() {
  yield call(initLD)
  yield spawn(monitorLDFeatures)
}

export function* monitorLDFeatures(): Generator {
  // Listener for any changes to the LD flags
  const webLDClient = ldClient as unknown as WebLDClient
  const changeChannel = eventChannel((emit) => {
    webLDClient.ldClientInstance.on('change', () => emit(true))
    return () => webLDClient.ldClientInstance.off('change')
  })

  try {
    do {
      // Ensures to grab override features
      const currentFeatures = yield select(selectFeatures)

      const initialFeatures = yield all(
        LD_FEATURES.reduce(
          (promiseDict: Record<string, unknown>, { name, ldKey, defaultValue }) => ({
            ...promiseDict,
            [name]: webLDClient.ldClientInstance.variation(ldKey, defaultValue),
          }),
          {}
        )
      )

      yield put(featuresActions.setLdFlags(initialFeatures))

      const overrides = currentFeatures?.overrides || {}
      const hasOverrides = overrides && Object.keys(overrides).length > 0

      const features = hasOverrides ? { ...initialFeatures, ...overrides } : { ...initialFeatures }

      yield put(featuresActions.setFeatures(features))
    } while (yield take(changeChannel))
  } finally {
    changeChannel.close()
  }
}
